/**
 * Copyright © 2025 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { Alert, Snackbar } from "@mui/material";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { subscribeToGlobalNotifications } from "../utils/globalNotification";
import { formatMessageWithReferenceId } from "../utils/requestCorrelation";

const NotificationContext = createContext();

const isStructuredNotification = (value) =>
  Boolean(
    value &&
      !Array.isArray(value) &&
      typeof value === "object" &&
      ("message" in value || "referenceId" in value || "severity" in value),
  );

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotificationContext must be used within an NotificationContextProvider"
    );
  }
  return context;
};
export const NotificationContextProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState();
  const [severity, setSeverity] = useState("success");

  // Stable across renders so consumers' error effects don't re-fire when the snackbar hides.
  const openNotification = useCallback((nextMessage, nextSeverity = "info") => {
    setMessage(nextMessage);
    setSeverity(nextSeverity);
    setOpen(true);
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeToGlobalNotifications(({ message: nextMessage, severity: nextSeverity = "info", referenceId }) => {
      openNotification(
        formatMessageWithReferenceId(nextMessage, referenceId),
        nextSeverity,
      );
    });

    return unsubscribe;
  }, []);

  const showNotification = useCallback((newMessage, newSeverity = "info") => {
    // Preserve legacy payloads, but format structured notifications with a reference id.
    if (isStructuredNotification(newMessage)) {
      openNotification(
        formatMessageWithReferenceId(newMessage.message, newMessage.referenceId),
        newMessage.severity || newSeverity,
      );
    } else {
      openNotification(newMessage, newSeverity);
    }
  }, [openNotification]);

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };
  return (
    <NotificationContext.Provider value={showNotification}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{
          pointerEvents: "none",
          top: "40%",
        }}
      >
        <Alert
          data-testid="global-notification-alert"
          onClose={handleClose}
          severity={severity}
          variant="filled"
          sx={{ width: "fit-content", pointerEvents: "auto", userSelect: "text" }}
        >
          {message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};
